import { MiloLimit, MiloProposalState, MiloUnavailableReason } from '@/enums/milo'
import { errorCode, errorDetails, parseError } from '@/libs/error'
import i18n from '@/libs/i18n'
import type { TagItem } from '@/types/Common'
import type { IMiloProposal } from '@/types/Milo'

/*
 * Milo in the console (docs/API.md §14): the fixed client texts of its error codes (§14.7) and
 * the badge of a proposal's state. The chat itself is `stores/milo.ts`.
 */

/** Milo's error codes (docs/API.md → Errors). */
export enum MiloError {
    TooManyRequests  = 11,
    Unavailable      = 47,
    LimitReached     = 48,
    ConversationFull = 49,
    Failed           = 50,
    ProposalClosed   = 51,
    Busy             = 52,
    VoiceUnavailable = 53,
    VoiceFailed      = 54,
}

/** What the person reads for a refusal of a message, a confirm or a stream's `error`. */
export const miloErrorText = (code: number, details: Record<string, unknown> = {}): string => {
    const t = i18n.global.t

    switch (code) {
        case MiloError.TooManyRequests:  return t('milo.error_rate')
        case MiloError.Unavailable:      return details.reason === MiloUnavailableReason.NotConfigured ? t('milo.error_not_configured') : t('milo.error_unavailable')
        case MiloError.LimitReached:     return details.limit === MiloLimit.Budget ? t('milo.error_budget') : t('milo.error_limit')
        case MiloError.ConversationFull: return t('milo.error_full')
        case MiloError.Failed:           return t('milo.error_failed')
        case MiloError.ProposalClosed:   return t('milo.error_closed')
        case MiloError.Busy:             return t('milo.error_busy')
        case MiloError.VoiceUnavailable: return t('milo.error_voice_unavailable')
        case MiloError.VoiceFailed:      return t('milo.error_voice_failed')
        case 0:                          return t('milo.error_network')
        default:                         return parseError({ error: { code, details } })
    }
}

/** The same, from an API error body. */
export const miloErrorOf = (body: unknown): { code: number, text: string } => {
    const code = errorCode(body)

    return { code, text: miloErrorText(code, errorDetails(body)) }
}

/** A model id as the contract allows it (§14.2): free text, the test's list is only a help. */
export const MODEL_PATTERN = /^[A-Za-z0-9._:-]{1,80}$/

/** A proposal's state as a badge. A pending one past its time reads as expired. */
export const proposalTag = (proposal: IMiloProposal, now = Date.now()): TagItem => {
    const t     = i18n.global.t
    const state = proposal.state === MiloProposalState.Pending && Date.parse(proposal.expires_at) < now
        ? MiloProposalState.Expired
        : proposal.state

    switch (state) {
        case MiloProposalState.Done:      return { label: t('milo.state_done'), color: 'success', icon: 'i-lucide-circle-check' }
        case MiloProposalState.Failed:    return { label: t('milo.state_failed'), color: 'error', icon: 'i-lucide-circle-x' }
        case MiloProposalState.Cancelled: return { label: t('milo.state_cancelled'), color: 'neutral', icon: 'i-lucide-ban' }
        case MiloProposalState.Expired:   return { label: t('milo.state_expired'), color: 'neutral', icon: 'i-lucide-clock' }
        default:                          return { label: t('milo.state_pending'), color: 'warning', icon: 'i-lucide-hand' }
    }
}

/** Whether Potrdi / Prekliči still apply. */
export const proposalOpen = (proposal: IMiloProposal, now = Date.now()): boolean =>
    proposal.state === MiloProposalState.Pending && !!proposal.token && Date.parse(proposal.expires_at) >= now
